import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Ticket, Copy, CheckCircle, ShoppingCart } from 'lucide-react';
import { get_customer_coupons } from '../../store/reducers/dashboardReducer';

const MyCoupons = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userInfo } = useSelector(s => s.auth);
    const { coupons }  = useSelector(s => s.dashboard);
    const [copied, setCopied] = useState(null);

    useEffect(() => {
        if (userInfo?.id) dispatch(get_customer_coupons(userInfo.id));
    }, [userInfo, dispatch]);

    const handleCopy = (code) => {
        navigator.clipboard.writeText(code);
        setCopied(code);
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="space-y-3">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        My Coupons
                    </span>
                </div>

                {coupons.length === 0 ? (
                    <div className="flex flex-col items-center py-16">
                        <Ticket className="w-10 h-10 text-gray-300 mb-2" />
                        <p className="text-sm font-medium text-gray-500">No coupons available</p>
                        <p className="text-xs text-gray-400 mt-1">Check back later for discount codes</p>
                    </div>
                ) : (
                    <div className="p-4 grid grid-cols-2 md:grid-cols-1 gap-3">
                        {coupons.map(coupon => {
                            const discountLabel =
                                coupon.type === 'fixed'
                                    ? `AED ${coupon.amount || 0} off`
                                    : `${coupon.amount || 0}% off`;
                            const isCopied = copied === coupon.code;

                            return (
                                <div
                                    key={coupon._id}
                                    className="border border-dashed border-orange-300 rounded-lg overflow-hidden"
                                >
                                    {/* Discount strip */}
                                    <div className="bg-orange-500 px-4 py-3">
                                        <p className="text-white/80 text-[10px] font-medium uppercase tracking-wide">
                                            Discount
                                        </p>
                                        <p className="text-white text-xl font-bold leading-tight">
                                            {discountLabel}
                                        </p>
                                    </div>

                                    {/* Code row */}
                                    <div className="px-4 py-3 bg-orange-50 flex items-center justify-between gap-3">
                                        <div>
                                            <p className="text-[10px] text-orange-400 font-medium uppercase tracking-wide">
                                                Code
                                            </p>
                                            <p className="text-base font-bold text-gray-900 font-mono tracking-wider">
                                                {coupon.code}
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-1.5 flex-shrink-0">
                                            <button
                                                onClick={() => handleCopy(coupon.code)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${
                                                    isCopied
                                                        ? 'bg-green-100 text-green-600'
                                                        : 'bg-white border border-orange-200 text-orange-500 hover:bg-orange-50'
                                                }`}
                                            >
                                                {isCopied
                                                    ? <><CheckCircle className="w-3 h-3" /> Copied!</>
                                                    : <><Copy className="w-3 h-3" /> Copy</>
                                                }
                                            </button>
                                            <button
                                                onClick={() => navigate('/card', { state: { prefillCoupon: coupon.code } })}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-all"
                                            >
                                                <ShoppingCart className="w-3 h-3" /> Use
                                            </button>
                                        </div>
                                    </div>

                                    {coupon.expiry && (
                                        <p className="px-4 py-2 text-[10px] text-gray-400 border-t border-orange-100">
                                            Expires{' '}
                                            {new Date(coupon.expiry).toLocaleDateString('en-AE', {
                                                day: 'numeric', month: 'short', year: 'numeric',
                                            })}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyCoupons;
